/* eslint-disable react-native/no-raw-text */

import React, { useCallback } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { NotificationFeedbackType } from 'expo-haptics';
import {
  BodyText,
  ChallengeCard,
  HStack,
  LoadingPlaceholder,
  slugify,
  TextLink,
  theme,
  triggerHapticNotificationFeedback,
  useResponsive,
} from '@sg/garnish';

import { useChallengesAndRewards } from '@order/ChallengesAndRewards';
import type { Challenge, ChallengeStatus } from '@order/graphql';
import {
  useChallengeOptInMutation,
  useChallengeWithThemeOptInMutation,
} from '@order/graphql';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import { useChallengesTelemetry } from '../../../../ChallengesScreen.telemetry';
import { IllusChallenges } from '../../assets';
import { ChallengesScreenInfoPanel } from '../ChallengesScreenInfoPanel';
import { ChallengesScreenSectionHeader } from '../ChallengesScreenSectionHeader';
import { useChallengeOptInErrorHandler } from './ChallengesScreenChallenges.hooks';

// ────────────────────────────────────────────────────────────────────────────────

export const ChallengesScreenChallenges = () => (
  <View style={styles.wrapper}>
    <ChallengesScreenChallengesContent />
  </View>
);

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const ChallengesScreenChallengesContent = () => {
  const { challenges, isFetchingChallenges, challengesError } =
    useChallengesAndRewards();

  const isLoadingChallenges = challenges === undefined || isFetchingChallenges;
  const hasAvailableChallenges =
    Array.isArray(challenges) && challenges.length > 0;
  const shouldRenderChallengesList =
    isLoadingChallenges || hasAvailableChallenges;

  // ─────────────────────────────────────────────────────────────────

  if (challengesError) return <ChallengesScreenChallengesError />;

  if (shouldRenderChallengesList) return <ChallengesScreenChallengesList />;

  return <ChallengesScreenNoChallenges />;
};

const ChallengesScreenChallengesList = () => {
  const { t } = useLocalizationContext();

  return (
    <View>
      <ChallengesScreenSectionHeader>
        {t('challenges.challenges.header')}
      </ChallengesScreenSectionHeader>

      <ChallengesScreenChallengesStack />
    </View>
  );
};

const ChallengesScreenChallengesStack = () => {
  const { challenges, isFetchingChallenges } = useChallengesAndRewards();
  const { match } = useResponsive();

  const isLoadingChallenges = challenges === undefined || isFetchingChallenges;
  const challengeCardsPerRow = match([1, 2, 3, 4]);

  if (isLoadingChallenges) {
    return (
      <View style={styles.challengesList}>
        <LoadingPlaceholder
          rows={1}
          palette="cream"
          columns={challengeCardsPerRow}
          rowHeight={350}
          gridGap={theme.spacing['6']}
        />
      </View>
    );
  }

  return (
    <HStack style={styles.challengesList} itemsPerRow={challengeCardsPerRow}>
      {challenges?.map((challenge, index) => {
        const testID = `${slugify(challenge.title)}-challenge-card`;

        return (
          <ChallengesScreenChallengeCard
            key={challenge.id}
            challenge={challenge}
            testID={testID}
            index={index}
          >
            <ChallengesScreenChallengesCardContent>
              {challenge.descriptiveTitle}
            </ChallengesScreenChallengesCardContent>
          </ChallengesScreenChallengeCard>
        );
      })}
    </HStack>
  );
};

const ChallengesScreenChallengeCard = (
  props: ChallengesScreenChallengeCardProps,
) => {
  const { challenge, children, index, testID } = props;
  const { expirationDate, id, progressCopy, status, title, goal, progress } =
    challenge;

  // ─── Challenge Opt-in Mutation ───────────────────────────────

  const shouldEnableFlashChallengeTheme = useFeatureFlag(
    'CELS-796-short-flash-challenge-card-theme-enabled',
  );

  const [
    challengeWithoutThemeOptInResponse,
    applyChallengeWithoutThemeOptInMutation,
  ] = useChallengeOptInMutation();
  const [
    challengeWithThemeOptInResponse,
    applyChallengeWithThemeOptInMutation,
  ] = useChallengeWithThemeOptInMutation();

  const challengeOptInResponse = shouldEnableFlashChallengeTheme
    ? challengeWithThemeOptInResponse
    : challengeWithoutThemeOptInResponse;
  const applyChallengeOptInMutation = shouldEnableFlashChallengeTheme
    ? applyChallengeWithThemeOptInMutation
    : applyChallengeWithoutThemeOptInMutation;

  const { fetching, error } = challengeOptInResponse;
  const { handleChallengesStartTelemetry } = useChallengesTelemetry();

  // ─────────────────────────────────────────────────────────────

  const isChallengeNotStarted = status === 'NOT_STARTED';
  const isChallengeStarted = checkIfChallengeIsStarted(status);

  const onStartChallenge = useCallback(async () => {
    handleChallengesStartTelemetry(id);

    // ─────────────────────────────────────────────────────────────────

    const { data: responseData } = await applyChallengeOptInMutation({
      challengeId: id,
    });
    const isSuccessfullyStartedChallenge = checkIfChallengeIsStarted(
      responseData?.challengeOptIn.status,
    );

    // ─── Haptic feedback ─────────────────────────────────────────────

    if (!isSuccessfullyStartedChallenge) {
      await triggerHapticNotificationFeedback(NotificationFeedbackType.Warning);

      return;
    }

    await triggerHapticNotificationFeedback(NotificationFeedbackType.Success);
  }, [id, handleChallengesStartTelemetry, applyChallengeOptInMutation]);

  useChallengeOptInErrorHandler(error);

  // ─────────────────────────────────────────────────────────────────

  if (isChallengeNotStarted) {
    return (
      <ChallengeCard.New
        index={index}
        onStartChallenge={onStartChallenge}
        title={title}
        expirationDate={expirationDate}
        isLoading={fetching}
        testID={`${testID}-new`}
      >
        {children}
      </ChallengeCard.New>
    );
  }

  if (isChallengeStarted) {
    return (
      <ChallengeCard.Ongoing
        index={index}
        totalSteps={goal}
        completedSteps={progress}
        title={title}
        expirationDate={expirationDate}
        footerText={progressCopy}
        testID={`${testID}-ongoing`}
      >
        {children}
      </ChallengeCard.Ongoing>
    );
  }

  return (
    <ChallengeCard.Completed
      title={title}
      expirationDate={expirationDate}
      testID={`${testID}-completed`}
      index={index}
    >
      {children}
    </ChallengeCard.Completed>
  );
};

const ChallengesScreenChallengesCardContent = (
  props: ChallengesScreenChallengesContentProps,
) => {
  const { t } = useLocalizationContext();
  const { handleChallengesTermsViewTelemetry } = useChallengesTelemetry();

  return (
    <BodyText size={4} style={styles.challengeContent}>
      {props.children}{' '}
      <TextLink
        size={4}
        href="https://www.sweetgreen.com/terms/"
        onPress={handleChallengesTermsViewTelemetry}
      >
        {t('general.terms-and-conditions')}
      </TextLink>{' '}
      {t('general.apply').toLowerCase()}.
    </BodyText>
  );
};

const ChallengesScreenNoChallenges = () => {
  const { t } = useLocalizationContext();

  return (
    <ChallengesScreenInfoPanel
      heading={t('challenges.challenges.header')}
      text={t('challenges.challenges.noChallengesText')}
      illustrationSource={IllusChallenges}
    />
  );
};

const ChallengesScreenChallengesError = () => {
  const { t } = useLocalizationContext();

  return (
    <ChallengesScreenInfoPanel
      heading={t('challenges.challenges.header')}
      text={t('challenges.challenges.error.network-error')}
      illustrationSource={IllusChallenges}
    />
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    paddingBottom: theme.spacing['10'],
  },
  challengeContent: {
    color: theme.colors.DARK_KALE,
  },
  challengesList: {
    paddingTop: theme.spacing['6'],
  },
});

// ─── Utils ──────────────────────────────────────────────────────────────────────

function checkIfChallengeIsStarted(status: ChallengeStatus | undefined) {
  if (!status) return false;

  return ['STARTED', 'IN_PROGRESS'].includes(status);
}

// ─── Types ──────────────────────────────────────────────────────────────────────

type ChallengesScreenChallengeCardProps = {
  readonly challenge: Challenge;
  readonly index: number;
} & Pick<ViewProps, 'testID' | 'children'>;

type ChallengesScreenChallengesContentProps = Pick<
  ViewProps,
  'testID' | 'children'
>;
